import clsx from 'clsx';
import { Menu, PanelLeftClose } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ROUTES } from '../../routes/routeConstants';

const navIconClass = 'w-5 h-5 shrink-0 transition-colors';

const Sidebar = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [isCollapsed, setIsCollapsed] = useState(false);
  // const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
  //   organizationManagement: true,
  //   support: false,
  // });

  // const toggleSection = (section: string) => {
  //   setExpandedSections((prev) => ({
  //     ...prev,
  //     [section]: !prev[section],
  //   }));
  // };

  const isActive = (path: string) => {
    return currentPath === path || currentPath.startsWith(`${path}/`);
  };

  const navItemClass = (active: boolean) =>
    clsx(
      'group relative flex items-center gap-3 py-2.5 px-4 text-base font-semibold transition-colors duration-150 w-full text-left rounded-none border-none no-underline cursor-pointer',
      isCollapsed && 'justify-center px-2.5',
      active
        ? 'bg-sidebar-active text-sidebar-active-text shadow-[inset_4px_0_0_0_var(--sidebar-active-accent)] [&_svg]:text-white'
        : 'bg-transparent text-sidebar-text hover:bg-sidebar-hover [&_svg]:text-sidebar-muted hover:[&_svg]:text-sidebar-text'
    );

  const navLabelClass =
    'truncate font-semibold whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0';

  return (
    <aside
      className={clsx(
        'flex flex-col h-screen overflow-hidden box-border relative shrink-0 border-r border-sidebar-border bg-sidebar-bg text-sidebar-text transition-[width] duration-300 ease-in-out',
        isCollapsed ? 'w-20' : 'w-[280px]'
      )}
    >
      <div
        className={clsx(
          'border-b border-sidebar-border shrink-0 flex items-center',
          isCollapsed ? 'justify-center py-4 px-2' : 'justify-between px-4 py-5 gap-3'
        )}
      >
        {!isCollapsed && (
          <div className='flex items-center justify-center w-full min-w-0 flex-1'>
            <img
              alt='Organization Logo'
              className='h-auto object-contain max-w-45 max-h-15 transition-all duration-200 ease-in-out'
              src='https://d15oq5oiy1uq8.cloudfront.net/org_logos/690075240014172235459509/1775731925_Updated Logo 6.png'
            />
          </div>
        )}
        <button
          type='button'
          className={clsx(
            'flex items-center justify-center border-none bg-transparent text-sidebar-muted cursor-pointer rounded-md transition-colors hover:bg-sidebar-hover hover:text-sidebar-text',
            isCollapsed ? 'w-full h-10' : 'w-8 h-8 shrink-0'
          )}
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <Menu size={20} strokeWidth={2.5} />
          ) : (
            <PanelLeftClose size={20} strokeWidth={2.5} />
          )}
        </button>
      </div>

      <nav className='flex-1 overflow-y-auto overflow-x-hidden py-4 px-0 scroll-smooth sidebar-scrollbar min-h-0'>
        <div className='mb-2'>
          <Link
            to={ROUTES.DASHBOARD}
            className={navItemClass(isActive(ROUTES.DASHBOARD))}
            aria-label='Dashboard'
          >
            <svg
              className={navIconClass}
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M3 4C3 3.44772 3.44772 3 4 3H8C8.55228 3 9 3.44772 9 4V9C9 9.55228 8.55228 10 8 10H4C3.44772 10 3 9.55228 3 9V4Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M11 4C11 3.44772 11.4477 3 12 3H16C16.5523 3 17 3.44772 17 4V6C17 6.55228 16.5523 7 16 7H12C11.4477 7 11 6.55228 11 6V4Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M11 12C11 11.4477 11.4477 11 12 11H16C16.5523 11 17 11.4477 17 12V16C17 16.5523 16.5523 17 16 17H12C11.4477 17 11 16.5523 11 16V12Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M3 13C3 12.4477 3.44772 12 4 12H8C8.55228 12 9 12.4477 9 13V16C9 16.5523 8.55228 17 8 17H4C3.44772 17 3 16.5523 3 16V13Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span className={clsx(navLabelClass, 'text-inherit', isCollapsed && 'hidden')}>
              Dashboard
            </span>
          </Link>
        </div>

        <div className='mb-2'>
          <Link
            to={ROUTES.ORGANIZATIONS}
            className={navItemClass(isActive(ROUTES.ORGANIZATIONS))}
            aria-label='Organizations'
          >
            <svg
              className={navIconClass}
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M3 6C3 4.89543 3.89543 4 5 4H7C8.10457 4 9 4.89543 9 6V14C9 15.1046 8.10457 16 7 16H5C3.89543 16 3 15.1046 3 14V6Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M11 6C11 4.89543 11.8954 4 13 4H15C16.1046 4 17 4.89543 17 6V10C17 11.1046 16.1046 12 15 12H13C11.8954 12 11 11.1046 11 10V6Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span className={clsx(navLabelClass, 'text-inherit', isCollapsed && 'hidden')}>
              Organizations
            </span>
          </Link>
        </div>

        <div className='mb-2'>
          <Link
            to={ROUTES.DEVICES}
            className={navItemClass(isActive(ROUTES.DEVICES))}
            aria-label='Devices'
          >
            <svg
              className={navIconClass}
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <rect
                x='7'
                y='2'
                width='10'
                height='20'
                rx='2'
                stroke='currentColor'
                strokeWidth='1.5'
              />
              <path d='M10 18h4' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
            </svg>
            <span className={clsx(navLabelClass, 'text-inherit', isCollapsed && 'hidden')}>
              Devices
            </span>
          </Link>
        </div>

        <div className='mb-2'>
          <Link
            to={ROUTES.USERS}
            className={navItemClass(isActive(ROUTES.USERS))}
            aria-label='Users'
          >
            <svg
              className={navIconClass}
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M3 17C3 14.2386 5.23858 12 8 12H12C14.7614 12 17 14.2386 17 17'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span className={clsx(navLabelClass, 'text-inherit', isCollapsed && 'hidden')}>
              Users
            </span>
          </Link>
        </div>

        <div className='mb-2'>
          <Link
            to={ROUTES.TOKEN_USAGE}
            className={navItemClass(isActive(ROUTES.TOKEN_USAGE))}
            aria-label='Token Usage'
          >
            <svg
              className={navIconClass}
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M12 6v6l4 2'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span className={clsx(navLabelClass, 'text-inherit', isCollapsed && 'hidden')}>
              Token Usage
            </span>
          </Link>
        </div>

        <div className='mb-2 hidden'>
          <Link
            to={ROUTES.AUDIT_TRAIL}
            className={navItemClass(isActive(ROUTES.AUDIT_TRAIL))}
            aria-label='Audit Trail'
          >
            <svg
              className={navIconClass}
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M9 12h6M9 16h4'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span className={clsx(navLabelClass, 'text-inherit', isCollapsed && 'hidden')}>
              Audit Trail
            </span>
          </Link>
        </div>

        <div className='mb-2'>
          <Link
            to={ROUTES.HELP_DESK}
            className={navItemClass(isActive(ROUTES.HELP_DESK))}
            aria-label='Help Desk'
          >
            <svg
              className={navIconClass}
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M12 17h.01'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span className={clsx(navLabelClass, 'text-inherit', isCollapsed && 'hidden')}>
              Help Desk
            </span>
          </Link>
        </div>

        {/* <div className='mb-2'>
          <button
            type='button'
            className='flex items-center justify-between w-full py-3 px-4 bg-transparent border-none cursor-pointer text-sidebar-text text-sm font-semibold transition-colors text-left hover:bg-sidebar-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sidebar-accent rounded-none'
            onClick={() => toggleSection('organizationManagement')}
          >
            <div className='flex items-center gap-3 flex-1 min-w-0'>
              <svg
                className='w-5 h-5 shrink-0 text-sidebar-muted'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M3 6C3 4.89543 3.89543 4 5 4H7C8.10457 4 9 4.89543 9 6V14C9 15.1046 8.10457 16 7 16H5C3.89543 16 3 15.1046 3 14V6Z'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M11 6C11 4.89543 11.8954 4 13 4H15C16.1046 4 17 4.89543 17 6V10C17 11.1046 16.1046 12 15 12H13C11.8954 12 11 11.1046 11 10V6Z'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <span
                className={clsx('truncate font-semibold text-inherit', isCollapsed && 'hidden')}
              >
                Organization Management
              </span>
            </div>
            <svg
              className={clsx(
                'w-4 h-4 shrink-0 text-sidebar-muted transition-transform duration-200',
                expandedSections.organizationManagement && 'rotate-180',
                isCollapsed && 'hidden'
              )}
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <path
                d='M4 6L8 10L12 6'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>

          {expandedSections.organizationManagement && (
            <div className='flex flex-col py-1'>
              <Link
                to={ROUTES.ORGANIZATIONS}
                className={navItemClass(isActive(ROUTES.ORGANIZATIONS))}
                aria-label='Organizations'
              >
                ...
              </Link>

              <Link
                to={ROUTES.USERS}
                className={navItemClass(isActive(ROUTES.USERS))}
                aria-label='Users'
              >
                ...
              </Link>
            </div>
          )}
        </div>

        <div className='mb-2'>
          <button
            type='button'
            className='...'
            onClick={() => toggleSection('support')}
          >
            Support
          </button>

          {expandedSections.support && (
            <div className='flex flex-col py-1'>
              <button type='button' className={navItemClass(false)} aria-label='Tickets'>
                Tickets
              </button>
            </div>
          )}
        </div> */}
      </nav>

      <div
        className={clsx(
          'px-4 py-5 border-t border-sidebar-border shrink-0 flex flex-col gap-1 bg-sidebar-bg items-center',
          isCollapsed ? 'px-2' : 'px-4'
        )}
      >
        <img
          src='/Humit-BlackBg.png'
          alt='Humit Logo'
          className={clsx('h-auto object-contain mb-1', isCollapsed ? 'max-w-[50px]' : 'max-w-30')}
        />

        <p
          className={clsx(
            'text-[11px] text-sidebar-muted m-0 font-normal text-center',
            isCollapsed && 'hidden'
          )}
        >
          Version: 1.0.0
        </p>
        <p
          className={clsx(
            'text-[10px] text-sidebar-muted m-0 font-normal text-center',
            isCollapsed && 'hidden'
          )}
        >
          Copyright © 2026, Novarroh Technologies, Inc
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
